/**
 * The app's one search field: the frame, the glyph, the term, and the way to empty it.
 *
 * ── Why it is shared, and why only now ───────────────────────────────────────────────────────
 * The clients roster and the plan library each drew their own, and they had already drifted: 300px
 * against 210, a 9px radius against 10, `--text` ink against `--text-3`, an sr-only name on the
 * clear button against an `aria-label`, and two search glyphs at different stroke weights. The
 * owner's 11 August disposition is that a trainer moving between the two screens meets ONE field,
 * identical and in the same place — so this is the promote-on-second-consumer rule met head on, and
 * the roster's form is the one both screens take.
 *
 * ── What a caller does NOT get to decide ─────────────────────────────────────────────────────
 * Its size, its radius, its surface, its ink, its focus ring, its glyphs, and the fact that Chrome's
 * native clear control is suppressed in favour of one that obeys the theme. Those are the identity
 * the disposition is about; `className` reaches the frame for the caller's own place in a toolbar
 * (margins, `align-self`) and for nothing else — no declaration in the module below is one a toolbar
 * has any reason to restate.
 *
 * ── Strings, not keys ────────────────────────────────────────────────────────────────────────
 * A `shared/` component may not know a feature's locale namespace, so the three strings arrive
 * resolved. The clear button's name is rendered as off-screen text rather than as an `aria-label`:
 * the glyph carries no visible label to contradict, and content-derived names survive a translation
 * the attribute would not.
 */

import styles from './SearchField.module.css';

interface SearchFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** The field's accessible name. The placeholder cannot be it — it disappears on the first keystroke. */
  readonly label: string;
  readonly placeholder: string;
  /** What the clear button is called. Spoken only; the ✕ is decorative. */
  readonly clearLabel: string;
  /** Dead while there is nothing to search — a pending or failed read. */
  readonly disabled: boolean;
  /** The caller's place for it in a toolbar. Layout only; see the header. */
  readonly className?: string;
  /**
   * `compact` is the ×0,8 cut KANON `Programy v2 - D3 (0.8)` draws in the plan library's 461px
   * master pane: 27px tall, a 10px term, and the pane's whole width. Everything else about the
   * field is identical, which is the point — the 11 August disposition is that a trainer meets ONE
   * search field, and a rescaled pane is not a second one.
   *
   * `fill` is the full-size field taking the width its row gives it rather than the stated 286px —
   * „Nowy program"'s template list, whose column is narrower than the field plus anything beside it.
   */
  readonly size?: 'default' | 'compact' | 'fill';
  /**
   * The `<input>` itself, for a screen that has to put focus in it from outside — the plan library
   * routes a bare printable key here, which is what a trainer typing over a list means by it.
   *
   * A named prop rather than the component's own `ref`, which React 19 would hand the wrapper: the
   * field is a frame around an input and only one of the two can be focused.
   */
  readonly inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * The two glyphs, drawn at the same 16-unit scale and the same 1.5 stroke, so the head and the tail
 * of one field cannot read as two different weights. Both `aria-hidden` — the input and the button
 * they sit in each carry their own name.
 */
function SearchGlyph() {
  return (
    <svg className={styles.glyph} viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
      <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10.6 10.6 14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ClearGlyph() {
  return (
    <svg className={styles.clearGlyph} viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
      <path
        d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SearchField({
  value,
  onChange,
  label,
  placeholder,
  clearLabel,
  disabled,
  className,
  size,
  inputRef,
}: SearchFieldProps) {
  return (
    <div
      className={[styles.frame, size === 'compact' ? styles.compact : size === 'fill' ? styles.fill : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
    >
      <SearchGlyph />
      <input
        ref={inputRef}
        type="search"
        className={styles.input}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={label}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      {/* Rendered only when there is something to clear, so it is never a control that does nothing. */}
      {value !== '' && (
        <button
          type="button"
          className={styles.clear}
          onClick={() => {
            onChange('');
          }}
        >
          <ClearGlyph />
          <span className={styles.hidden}>{clearLabel}</span>
        </button>
      )}
    </div>
  );
}
